import { Component, OnDestroy, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { DateTime } from 'luxon';
import * as pako from 'pako';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import { environment } from '../../../environments/environments';
import { Apiservice } from '../../../services/ApiService';
import { AuthService } from '../../../services/AuthService';
import { StorageService } from '../../../services/StorageService';
import { VenueService } from '../../../services/VenueService';
import { VenueEntry } from '../../../models/venue-admin.model';

/** Per-result-code reject counts, keyed by code (e.g. `EVENT_RESTRICTION`). */
type RejectCounts = Record<string, number>;

/**
 * Headline figures for a single venue, derived from its attendance summary.
 * Mirrors the hero tile on the attendance "Reports" page so the dashboard reads
 * as a multi-venue overview of the same source of truth. The same row also
 * backs the Venue Totals table, so it carries every column that table shows.
 */
interface VenueDashboardCard {
  key: string;
  name: string;
  /** Venue-local report date (yyyy-MM-dd), used for both display and the API call. */
  date: string;
  timezone: string;
  entries: number;
  exits: number;
  internal: number;
  reentries: number;
  inVenue: number;
  scanRate: number;
  rejects: RejectCounts;
}

/** Sort direction for the Venue Totals table headers. */
type SortDir = 'asc' | 'desc';

/**
 * Ticketmaster-only overview page: one headline entry summary per actively
 * scanning venue. "Active" reuses the same rolling-window definition as the
 * attendance picker's "Scanning now" group (see {@link VenueService}).
 *
 * The dashboard is read-only — clicking a card hands off to the full attendance
 * report for that venue. Per-card data drawers are intentionally deferred.
 */
@Component({
  selector: 'app-dashboard',
  standalone: false,
  templateUrl: './dashboard.component.html',
  styleUrls: ['./dashboard.component.scss'],
})
export class DashboardComponent implements OnInit, OnDestroy {
  cards: VenueDashboardCard[] = [];
  loading: boolean = true;
  /** Set when the venue/attendance lookups fail outright so the view can explain itself. */
  loadError: boolean = false;
  /** True while a live socket is connected and streaming updates. */
  live: boolean = false;

  /** Whether the "Get historical data" modal is open. */
  historicalOpen: boolean = false;

  /** Dark-mode preference, persisted across sessions and applied via a body class. */
  useDarkMode: boolean = false;
  private static readonly DARK_MODE_KEY = 'dashboardUseDarkMode';
  private static readonly DARK_BODY_CLASS = 'dashboard-dark';

  /** Venue Totals table: rows sorted by the active header. */
  sortedRows: VenueDashboardCard[] = [];
  /** Union of reject codes across all venues, driving the expandable reject columns. */
  rejectColumns: string[] = [];
  /** Whether the per-code reject breakdown columns are expanded. */
  rejectsExpanded: boolean = false;
  /** Active sort column key and direction for the table. */
  sortKey: string = 'entries';
  sortDir: SortDir = 'desc';

  private subscription: Subscription | null = null;

  /**
   * Periodic refresh of the active-venue set and headline figures, so the
   * dashboard stays current even if a venue starts/stops scanning while the
   * page is open. Runs silently (no spinner, no blanking on error); the live
   * socket is only restarted when the active-venue set actually changes.
   */
  private readonly ACTIVE_POLL_MS = 5 * 60 * 1000;
  private pollTimerId: ReturnType<typeof setInterval> | null = null;
  private pollSubscription: Subscription | null = null;

  /** Single multi-venue socket; see docs/websocketSubscribe.md. */
  private socket: WebSocket | null = null;
  /** Distinguishes an intentional teardown from a dropped connection that should reconnect. */
  private manualSocketClose: boolean = false;
  /** Card lookup by venue key, so an incoming push routes to its tile in O(1). */
  private cardsByKey: Map<string, VenueDashboardCard> = new Map();

  /**
   * Message-idle watchdog. A socket can stop delivering frames without firing
   * `onclose` (a half-open connection), so the close-based reconnect never
   * triggers. If no frame arrives within the current idle window while live, we
   * force a reconnect. The window starts at {@link SOCKET_IDLE_BASE_MS} and
   * doubles on each idle-triggered reconnect up to {@link SOCKET_IDLE_MAX_MS},
   * so a genuinely quiet venue isn't reconnected every few seconds; any frame
   * resets it back to the base.
   */
  private readonly SOCKET_IDLE_BASE_MS = 5000;
  private readonly SOCKET_IDLE_MAX_MS = 320000;
  private socketIdleMs: number = this.SOCKET_IDLE_BASE_MS;
  private idleTimerId: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly apiService: Apiservice,
    private readonly venueService: VenueService,
    private readonly storage: StorageService,
    private readonly auth: AuthService,
    private readonly router: Router
  ) {}

  ngOnInit(): void {
    this.useDarkMode = localStorage.getItem(DashboardComponent.DARK_MODE_KEY) === 'true';
    this.applyDarkModeToBody();
    this.loadDashboard();
  }

  ngOnDestroy(): void {
    this.subscription?.unsubscribe();
    this.stopPolling();
    this.closeSocket();
    // Don't leave the dark class on the body once we navigate away.
    document.body.classList.remove(DashboardComponent.DARK_BODY_CLASS);
  }

  /** Flips the dark-mode preference, persisting it and re-applying the body class. */
  toggleDarkMode(): void {
    this.useDarkMode = !this.useDarkMode;
    localStorage.setItem(DashboardComponent.DARK_MODE_KEY, String(this.useDarkMode));
    this.applyDarkModeToBody();
  }

  private applyDarkModeToBody(): void {
    document.body.classList.toggle(DashboardComponent.DARK_BODY_CLASS, this.useDarkMode);
  }

  /** True once loading has finished and no venues were scanning in the window. */
  get isEmpty(): boolean {
    return !this.loading && !this.loadError && this.cards.length === 0;
  }

  /**
   * Initial load: shows the spinner, fetches the active venues and their
   * figures, opens the live socket, and starts the periodic refresh.
   */
  private loadDashboard(): void {
    this.loading = true;
    this.loadError = false;

    this.subscription = this.fetchActiveCards().subscribe({
      next: (cards) => {
        this.applyCards(cards);
        this.loading = false;
        this.startLiveUpdates();
        this.startPolling();
      },
      error: () => {
        this.loadError = true;
        this.loading = false;
        // Still poll: a later tick may recover once the backend is reachable.
        this.startPolling();
      },
    });
  }

  /**
   * Resolves the active venues, then fans out one attendance request per venue
   * for that venue's local day. Per-venue failures resolve to `null` and are
   * filtered out; a failure of the venue/active-scan lookups themselves
   * propagates as an error for the caller to handle (so a transient poll
   * failure can leave the last good figures in place rather than blanking).
   */
  private fetchActiveCards() {
    return forkJoin({
      venues: this.venueService.getVenueList(),
      activeKeys: this.venueService.getActiveScanVenueIds(),
    }).pipe(
      switchMap(({ venues, activeKeys }) => {
        const activeVenues = venues.filter((venue) => activeKeys.has(venue.key));
        if (activeVenues.length === 0) {
          return of([] as VenueDashboardCard[]);
        }

        const cardRequests = activeVenues.map((venue) => this.loadVenueCard(venue));
        return forkJoin(cardRequests).pipe(
          map((cards) => cards.filter((card): card is VenueDashboardCard => card !== null))
        );
      })
    );
  }

  /** Sorts the cards busiest-first and rebuilds the lookup map and table. */
  private applyCards(cards: VenueDashboardCard[]): void {
    this.cards = cards.sort((a, b) => b.entries - a.entries);
    this.cardsByKey = new Map(this.cards.map((card) => [card.key, card]));
    this.refreshTable();
  }

  /**
   * Starts the 5-minute silent refresh. Each tick re-fetches the active venues
   * and figures; the live socket is reconnected only when the set of active
   * venue keys actually changes, so a steady set doesn't churn the connection.
   */
  private startPolling(): void {
    this.stopPolling();
    this.pollTimerId = setInterval(() => this.pollActiveCards(), this.ACTIVE_POLL_MS);
  }

  private stopPolling(): void {
    if (this.pollTimerId !== null) {
      clearInterval(this.pollTimerId);
      this.pollTimerId = null;
    }
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = null;
  }

  /**
   * One silent refresh tick. Doesn't toggle the loading spinner or surface
   * errors (a transient failure just leaves the last good figures in place).
   * Only reconnects the socket if the active-venue set changed.
   */
  private pollActiveCards(): void {
    const previousKeys = this.venueKeySignature();
    this.pollSubscription?.unsubscribe();
    this.pollSubscription = this.fetchActiveCards().subscribe({
      next: (cards) => {
        this.applyCards(cards);
        // A successful refresh clears any earlier error banner.
        this.loadError = false;
        if (this.venueKeySignature() !== previousKeys) {
          this.restartLiveUpdates();
        }
      },
      // Swallow transient errors: keep the last good figures and try next tick.
      error: () => {},
    });
  }

  /** Stable signature of the current card venue keys, for change detection. */
  private venueKeySignature(): string {
    return this.cards
      .map((card) => card.key)
      .sort()
      .join('|');
  }

  /**
   * Fetches a single venue's attendance for its local day. A failed venue
   * resolves to `null` rather than rejecting so one bad venue can't blank the
   * whole dashboard.
   */
  private loadVenueCard(venue: VenueEntry) {
    const timezone = venue.value.timezone || 'UTC';
    const date = DateTime.now().setZone(timezone).toFormat('yyyy-MM-dd');
    const encodedId = encodeURIComponent(venue.key);

    return this.apiService.getAttendance(encodedId, date, '').pipe(
      map((data: any): VenueDashboardCard => ({
        key: venue.key,
        name: venue.value.name,
        date,
        timezone,
        ...this.extractCounts(data),
        scanRate: this.computeScanRate(data),
      })),
      catchError(() => of(null))
    );
  }

  /**
   * Pulls the headline counts and reject map from a venue's attendance summary.
   * Values arrive as strings on the wire, so each is coerced to a number; the
   * reject map is copied as-is for the per-code columns.
   */
  private extractCounts(summary: any): {
    entries: number;
    exits: number;
    internal: number;
    reentries: number;
    inVenue: number;
    rejects: RejectCounts;
  } {
    return {
      entries: Number(summary?.entries || 0),
      exits: Number(summary?.exits || 0),
      internal: Number(summary?.internal || 0),
      reentries: Number(summary?.reentries || 0),
      inVenue: Number(summary?.inVenue || 0),
      rejects: (summary?.rejects as RejectCounts) || {},
    };
  }

  /**
   * Total scans-per-minute across all events, summing the success and error
   * rates the backend reports per event. Matches the attendance hero tile's
   * `totalScanRate` so the figures agree between the two pages.
   */
  private computeScanRate(summary: any): number {
    const events = summary?.events || {};
    let rate = 0;
    for (const eventCode of Object.keys(events)) {
      const event = events[eventCode] || {};
      rate += Number(event.successScanRate || 0) + Number(event.errorScanRate || 0);
    }
    return Math.round(rate * 10) / 10;
  }

  /**
   * Opens a single socket and subscribes it to every card's venue, so the
   * headline numbers update live without polling. The connection follows the
   * multi-venue model in docs/websocketSubscribe.md: connect with the first
   * venue, then send a `subscribe` message for each of the rest.
   */
  private async startLiveUpdates(): Promise<void> {
    if (this.cards.length === 0) return;

    const token = await this.getAccessToken();
    if (!token) return;

    // Connect bound to the first venue; the remainder are added via subscribe.
    const [firstCard, ...restCards] = this.cards;
    const wsEndpoint =
      environment.wsEndpoint + token + '&venue=' + encodeURIComponent(firstCard.key);

    this.manualSocketClose = false;
    const socket = new WebSocket(wsEndpoint);
    this.socket = socket;

    socket.onopen = () => {
      this.live = true;
      for (const card of restCards) {
        socket.send(
          JSON.stringify({ action: 'subscribe', venue: card.key, Auth: token })
        );
      }
      // Start watching for a stalled (half-open) connection.
      this.armIdleWatchdog();
    };

    socket.onmessage = (event) => {
      // A live frame means the connection is healthy: reset the idle window to
      // its base and re-arm the watchdog.
      this.socketIdleMs = this.SOCKET_IDLE_BASE_MS;
      this.armIdleWatchdog();
      this.handleSocketMessage(event.data);
    };

    socket.onclose = () => {
      this.live = false;
      this.clearIdleWatchdog();
      // Reconnect (and re-subscribe to every venue) unless we closed on purpose.
      if (!this.manualSocketClose) {
        this.startLiveUpdates();
      }
    };

    socket.onerror = () => {
      this.live = false;
    };
  }

  /**
   * Tears down the current socket and opens a fresh one bound to the updated
   * card set. Used when a poll changes which venues are active, so the socket's
   * subscriptions track the cards on screen. The old socket's handlers are
   * detached first so its `onclose` can't trigger a competing reconnect.
   */
  private restartLiveUpdates(): void {
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }
    this.clearIdleWatchdog();
    this.live = false;
    // Reset the idle back-off so the new connection starts from the base window.
    this.socketIdleMs = this.SOCKET_IDLE_BASE_MS;
    this.startLiveUpdates();
  }

  /**
   * (Re)arms the message-idle watchdog for the current idle window. If it fires,
   * the socket has gone quiet while live, so we back off (double the window, up
   * to the cap) and force a reconnect.
   */
  private armIdleWatchdog(): void {
    this.clearIdleWatchdog();
    this.idleTimerId = setTimeout(() => {
      this.socketIdleMs = Math.min(this.socketIdleMs * 2, this.SOCKET_IDLE_MAX_MS);
      this.reconnectFromIdle();
    }, this.socketIdleMs);
  }

  private clearIdleWatchdog(): void {
    if (this.idleTimerId !== null) {
      clearTimeout(this.idleTimerId);
      this.idleTimerId = null;
    }
  }

  /**
   * Tears down a stalled socket and opens a fresh one. The old socket's handlers
   * are detached first so its eventual `onclose` can't trigger a second,
   * competing reconnect.
   */
  private reconnectFromIdle(): void {
    this.live = false;
    if (this.socket) {
      this.socket.onopen = null;
      this.socket.onmessage = null;
      this.socket.onclose = null;
      this.socket.onerror = null;
      this.socket.close();
      this.socket = null;
    }
    this.startLiveUpdates();
  }

  /** Resolves the JWT used both to connect and to authorise each subscribe. */
  private async getAccessToken(): Promise<string | undefined> {
    const session = await this.auth.getCurrentSession();
    return session?.accessToken?.toString();
  }

  /**
   * Decodes an inbound frame and applies it to the matching card. The `main`
   * report arrives as plain JSON; other report types arrive gzip + base64, so
   * we mirror the attendance page's decode path. Status frames (e.g. a 401/403
   * subscribe rejection) aren't attendance payloads and are simply ignored.
   */
  private async handleSocketMessage(data: any): Promise<void> {
    try {
      let text: string | null = null;

      if (typeof data === 'string') {
        text = this.maybeUngzipBase64(data);
      } else if (data instanceof Blob) {
        text = this.bytesToText(new Uint8Array(await data.arrayBuffer()));
      } else if (data instanceof ArrayBuffer) {
        text = this.bytesToText(new Uint8Array(data));
      } else {
        return;
      }

      if (!text || !(text.startsWith('{') || text.startsWith('['))) return;

      this.applySocketPayload(JSON.parse(text));
    } catch (error) {
      console.error('Error handling dashboard socket message:', error);
    }
  }

  /** Decodes a base64+gzip frame to text, passing plain JSON through untouched. */
  private maybeUngzipBase64(input: string): string | null {
    const trimmed = input.trim();
    if (!trimmed) return null;
    if (trimmed.startsWith('{') || trimmed.startsWith('[')) {
      return trimmed;
    }
    if (!/^[A-Za-z0-9+/=\s]+$/.test(trimmed)) {
      return trimmed;
    }
    try {
      const binary = atob(trimmed.replace(/\s+/g, ''));
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
      return this.bytesToText(bytes);
    } catch {
      return trimmed;
    }
  }

  /** Gunzips when the gzip magic bytes are present, otherwise decodes as UTF-8. */
  private bytesToText(bytes: Uint8Array): string {
    if (bytes.length >= 2 && bytes[0] === 0x1f && bytes[1] === 0x8b) {
      return pako.ungzip(bytes, { to: 'string' });
    }
    return new TextDecoder('utf-8').decode(bytes);
  }

  /**
   * Routes a live attendance payload to its card and refreshes the headline
   * figures. Pushes are per-venue, so the payload's venue is read from
   * `attendID` (format `<venueKey>_<date>`) and matched against a known card.
   */
  private applySocketPayload(payload: any): void {
    const venueKey = this.resolveVenueKey(payload);
    if (!venueKey) return;

    const card = this.cardsByKey.get(venueKey);
    if (!card) return;

    Object.assign(card, this.extractCounts(payload));
    // Timed frames don't carry `events`; recompute the rate only from `main`
    // frames so the card doesn't flicker to 0 between them.
    if (payload.events !== undefined) {
      card.scanRate = this.computeScanRate(payload);
    }
    // Keep the busiest venues leading and the table in sync as numbers move.
    this.cards = [...this.cards].sort((a, b) => b.entries - a.entries);
    this.refreshTable();
  }

  /**
   * Extracts the venue key from a live payload. The `attendID`
   * (`<venueKey>_<date>`) is the reliable source — the top-level `venue` field
   * is an empty object on `main` reports, so it's only trusted when it's a
   * non-empty string.
   */
  private resolveVenueKey(payload: any): string | null {
    if (typeof payload?.venue === 'string' && payload.venue) {
      return payload.venue;
    }
    const attendID = payload?.attendID;
    if (!attendID) return null;
    const id = String(attendID);
    const splitAt = id.lastIndexOf('_');
    return splitAt > 0 ? id.substring(0, splitAt) : id;
  }

  /** Tears the socket down for good (e.g. on navigation away). */
  private closeSocket(): void {
    this.manualSocketClose = true;
    this.live = false;
    this.clearIdleWatchdog();
    if (this.socket) {
      this.socket.close();
      this.socket = null;
    }
  }

  // ---------------------------------------------------------------------------
  // Venue Totals table
  //
  // Mirrors the attendance page's Venue Totals table, but each row is an active
  // venue (rather than a child venue of one selected venue). Columns, sorting,
  // and the expandable reject breakdown match that table; see
  // attendance.component for the original.
  // ---------------------------------------------------------------------------

  /** Rebuilds the reject-code columns and re-sorts the rows from the current cards. */
  private refreshTable(): void {
    const codes = new Set<string>();
    for (const card of this.cards) {
      for (const code of Object.keys(card.rejects)) {
        codes.add(code);
      }
    }
    this.rejectColumns = Array.from(codes).sort();
    this.sortedRows = this.sortRows(this.cards);
  }

  /** The "All Venues Total" row: every count and reject code summed across venues. */
  get totalsRow(): {
    entries: number;
    exits: number;
    internal: number;
    reentries: number;
    inVenue: number;
    rejects: RejectCounts;
  } {
    const totals = {
      entries: 0,
      exits: 0,
      internal: 0,
      reentries: 0,
      inVenue: 0,
      rejects: {} as RejectCounts,
    };
    for (const card of this.cards) {
      totals.entries += card.entries;
      totals.exits += card.exits;
      totals.internal += card.internal;
      totals.reentries += card.reentries;
      totals.inVenue += card.inVenue;
      for (const code of Object.keys(card.rejects)) {
        totals.rejects[code] = (totals.rejects[code] || 0) + Number(card.rejects[code] || 0);
      }
    }
    return totals;
  }

  /** Toggles the sort column/direction and re-sorts. Name sorts ascending first; counts descending. */
  toggleSort(key: string): void {
    if (this.sortKey === key) {
      this.sortDir = this.sortDir === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortKey = key;
      this.sortDir = key === 'name' ? 'asc' : 'desc';
    }
    this.sortedRows = this.sortRows(this.cards);
  }

  /** The ▲/▼ glyph for the active sort column, blank for the rest. */
  sortIndicator(key: string): string {
    if (this.sortKey !== key) return '';
    return this.sortDir === 'asc' ? '▲' : '▼';
  }

  toggleRejectsExpanded(): void {
    this.rejectsExpanded = !this.rejectsExpanded;
  }

  /** Turns a raw reject code like `EVENT_RESTRICTION` into a readable `Event Restriction`. */
  formatErrorCode(code: string): string {
    if (!code) return '';
    return code
      .toLowerCase()
      .split(/[_\s]+/)
      .filter(Boolean)
      .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  /** Total rejects for a row across all codes. */
  rejectTotal(rejects: RejectCounts | undefined | null): number {
    if (!rejects) return 0;
    let total = 0;
    for (const code of Object.keys(rejects)) {
      total += Number(rejects[code] || 0);
    }
    return total;
  }

  /** Reject count for a single code, or 0 when absent. */
  rejectCount(rejects: RejectCounts | undefined | null, code: string): number {
    if (!rejects) return 0;
    return Number(rejects[code] || 0);
  }

  /** Rejects as a percentage of all scan activity (entries + exits + internal + reentries). */
  rejectPercent(row: { entries: number; exits: number; internal: number; reentries: number; rejects: RejectCounts }): number {
    const rejects = this.rejectTotal(row.rejects);
    if (!rejects) return 0;
    const denom = row.entries + row.exits + row.internal + row.reentries;
    if (!denom) return 0;
    return (rejects / denom) * 100;
  }

  /** Sorts a copy of the rows by the active column/direction, leaving `cards` untouched. */
  private sortRows(rows: VenueDashboardCard[]): VenueDashboardCard[] {
    const key = this.sortKey;
    const dir = this.sortDir;
    return [...rows].sort((a, b) => {
      let av: number | string;
      let bv: number | string;
      if (key === 'name') {
        av = a.name;
        bv = b.name;
      } else if (key === '__rejectPct') {
        av = this.rejectPercent(a);
        bv = this.rejectPercent(b);
      } else if (key === '__rejectTotal') {
        av = this.rejectTotal(a.rejects);
        bv = this.rejectTotal(b.rejects);
      } else if (key.startsWith('__reject:')) {
        const code = key.slice('__reject:'.length);
        av = this.rejectCount(a.rejects, code);
        bv = this.rejectCount(b.rejects, code);
      } else {
        av = (a as any)[key] ?? 0;
        bv = (b as any)[key] ?? 0;
      }
      const cmp =
        typeof av === 'number' && typeof bv === 'number'
          ? av - bv
          : String(av).localeCompare(String(bv));
      return dir === 'asc' ? cmp : -cmp;
    });
  }

  /** Formats a venue-local date as e.g. "Mon 15th June 2026", matching the hero tile. */
  formatCardDate(card: VenueDashboardCard): string {
    const dt = DateTime.fromISO(card.date, { zone: card.timezone || 'UTC' });
    if (!dt.isValid) return card.date;
    const day = dt.day;
    const suffix =
      day % 10 === 1 && day !== 11 ? 'st'
      : day % 10 === 2 && day !== 12 ? 'nd'
      : day % 10 === 3 && day !== 13 ? 'rd'
      : 'th';
    return `${dt.toFormat('ccc')} ${day}${suffix} ${dt.toFormat('LLLL yyyy')}`;
  }

  /**
   * Hands off to the full attendance report for the chosen venue. The
   * attendance page restores its venue from storage on load, so we persist the
   * selection the same way the picker does before navigating.
   */
  openVenue(card: VenueDashboardCard): void {
    this.storage.saveVenue(card.key);
    this.router.navigate(['/attendance']);
  }

  openHistorical(): void {
    this.historicalOpen = true;
  }

  closeHistorical(): void {
    this.historicalOpen = false;
  }
}
